import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { debounce, isEqual, max } from "lodash";
import { Dimensions, Platform } from "react-native";
import { Color } from "../../types";
import { config } from "../config";

type ContrastOption = "light" | "dark";
type RBGAColor = string;
type RBGColorComponents = { r: number; g: number; b: number };

export type ThemeResponse = {
  logoUrl?: string;
  customHeaderImageUrl?: string;
  backgroundImageUrl?: string;
  background: Color;
  backgroundText: Color;
  accent: Color;
  accentText: Color;
  announcementBar: Color;
  announcementBarText: Color;
  themeContrast: ContrastOption;
  deviceScale: number;
  deviceScalingEnabled: boolean;
};

const spacing = {
  none: 0,
  tiny: 2,
  xxsmall: 4,
  xsmall: 8,
  small: 10,
  medium: 20,
  regular: 32,
  large: 40,
  xlarge: 80,
};

export type Spacing = keyof typeof spacing;

const iconSize = {
  small: 12,
  regular: 16,
  medium: 24,
  large: 40,
  larger: 60,
  xlarge: 100,
};

export type IconSize = keyof typeof iconSize;

const fontSize = {
  detail: 12,
  small: 14,
  body: 16,
  medium: 18,
  large: 24,
  h4: 28,
  h3: 40,
  h2: 62,
  h1: 90,
};

export type FontSize = keyof typeof fontSize;

const borderRadius = {
  small: 5,
  medium: 16,
  large: 24,
};

export type BorderRadius = keyof typeof borderRadius;

const borderWidth = {
  small: 1,
  regular: 3,
  large: 5,
};

export type BorderWidth = keyof typeof borderWidth;

const colors = {
  white: "#FFFFFF" as Color,
  black: "#000000" as Color,
  grey1: "#636464" as Color,
  grey2: "#121212" as Color,
  blue: "#007AFE" as Color,
  red: "#CA0B00" as Color,
};

export const defaultTheme: ThemeResponse = {
  background: colors.black,
  backgroundText: colors.white,
  accent: colors.blue,
  accentText: colors.white,
  announcementBar: colors.red,
  announcementBarText: colors.white,
  themeContrast: "light",
  deviceScale: 1,
  deviceScalingEnabled: true,
};

type ThemeOptionsWithContrastColor = ThemeResponse & {
  contrastColor: Color;
};

export type ThemeOptions = ThemeOptionsWithContrastColor & {
  spacing: typeof spacing;
  iconSize: typeof iconSize;
  fontSize: typeof fontSize;
  borderRadius: typeof borderRadius;
  borderWidth: typeof borderWidth;
  colors: typeof colors;
  scaled: (num: number) => number;
  toRgb: (hex: Color) => RBGColorComponents;
  toRgba: (hex: Color, opacity: number) => RBGAColor;
};

const ThemeContext = createContext<{
  theme: ThemeOptions;
  setTheme: (theme: ThemeResponse) => void;
}>(null!);

const getScale = () => {
  const maxDimension = max([
    Dimensions.get("window").width,
    Dimensions.get("window").height,
  ]) as number;
  return parseFloat((maxDimension / 1500).toFixed(2));
};

export const ThemeProvider = (props: PropsWithChildren) => {
  const [resolutionScale, setResolutionScale] = useState(getScale());
  const [theme, setTheme] = useState<ThemeOptionsWithContrastColor>({
    ...defaultTheme,
    contrastColor: colors.white,
  });

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleResize = debounce(() => setResolutionScale(getScale()), 300);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const scaled = (num: number) => {
    const scale = theme.deviceScalingEnabled
      ? resolutionScale * theme.deviceScale
      : config.pixelDensity;
    return Math.round(num * scale);
  };

  const objectScaled = <T extends Record<string, number>>(obj: T): T =>
    Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, scaled(value)]),
    ) as T;

  const handleThemeChange = useCallback(
    (themeResponse: ThemeResponse) => {
      const nextTheme = {
        ...themeResponse,
        contrastColor:
          themeResponse.themeContrast === "light" ? colors.white : colors.black,
      };

      if (!isEqual(nextTheme, theme)) {
        setTheme(nextTheme);
      }
    },
    [theme],
  );

  const toRgb = (hex: string): RBGColorComponents => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
      hex?.toLowerCase(),
    );
    if (result == null) {
      throw new Error(`Hex: ${hex}`);
    }
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };
  };

  const toRgba = (hex: string, opacity: number): RBGAColor => {
    const { r, g, b } = toRgb(hex);
    return `rgba(${r},${g},${b},${opacity})` as RBGAColor;
  };

  return (
    <ThemeContext.Provider
      value={{
        theme: {
          ...theme,
          spacing: objectScaled(spacing),
          iconSize: objectScaled(iconSize),
          fontSize: objectScaled(fontSize),
          borderRadius: objectScaled(borderRadius),
          borderWidth: objectScaled(borderWidth),
          colors,
          scaled,
          toRgb,
          toRgba,
        },
        setTheme: handleThemeChange,
      }}
      {...props}
    />
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (ctx == null) {
    throw new Error("useTheme: context not ready");
  }

  return ctx.theme;
};

export const useSetTheme = () => {
  const ctx = useContext(ThemeContext);
  if (ctx == null) {
    throw new Error("useTheme: context not ready");
  }

  return ctx.setTheme;
};
